import React, { useState, useEffect } from "react";
import {
  MapPin,
  Star,
  ChevronRight,
  ChevronLeft,
  Camera,
  Heart,
  Loader
} from "lucide-react";
import NavBar from "../components/NavBar";

const PropertiesPage = () => {
  const colors = {
    brown: "#A3582A", // Takeaya Brown
    green: "#8C8660", // Takeaya Green
    beige: "#EFE5C6", // Takeaya Beige
    yellow: "#E9BA67", // Takeaya Yellow
    lightGreen: "#E4E2D5", // Takeaya Light Green
    gray: "#5E6065", // Takeaya Gray
  };
  
  // State for managing properties, loading and error states
  const [properties, setProperties] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    lastPage: 1,
    total: 0
  });
  
  // Fetch properties from API
  const fetchProperties = async (page = 1) => {
    try {
      setIsLoading(true);
      
      const response = await fetch(`https://admin.takeaya.com/api/admin/properties?page=${page}`);
      
      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.success && result.data) {
        // Transform the API data to match our component's expected format
        const transformedProperties = result.data.map(property => ({
          id: property.id,
          name: property.name,
          location: property.location,
          price: `${property.currency} ${property.price}`,
          rating: 4.8, // Default rating since API doesn't provide it
          // Map the images array from the API
          images: property.images.map(img => 
            // Create full URLs for the images - adjust the base URL as needed
            `${img.image}`
          ),
          description: property.description,
          bedrooms: 3, // Default values since API doesn't provide these
          bathrooms: 2,
          sqft: 2000,
          featured: true
        }));
        
        // Always set properties (not appending) since we're navigating to specific pages
        setProperties(transformedProperties);
        
        // Update pagination state
        if (result.pagination) {
          setPagination({
            currentPage: result.pagination.current_page,
            lastPage: result.pagination.last_page,
            total: result.pagination.total
          });
        }
      } else {
        throw new Error('No data returned from API');
      }
    } catch (err) {
      console.error('Error fetching properties:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle page change
  const handlePageChange = (page) => {
    if (page !== pagination.currentPage) {
      window.scrollTo(0, 0); // Scroll to top when changing pages
      fetchProperties(page);
    }
  };
  
  // Initial data fetch
  useEffect(() => {
    fetchProperties();
  }, []);

  /* Page Number Component */
  const PageNumber = ({ page, currentPage, onClick }) => {
    const isActive = page === currentPage;
    
    return (
      <button
        className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors duration-300 ${
          isActive 
            ? 'text-white' 
            : 'text-gray-600 hover:bg-primary/10'
        }`}
        style={{ 
          backgroundColor: isActive ? colors.brown : 'transparent',
          borderWidth: !isActive ? '1px' : '0px',
          borderColor: !isActive ? colors.brown + '40' : 'transparent',
          borderStyle: 'solid'
        }}
        onClick={() => onClick(page)}
        disabled={isActive}
      >
        {page}
      </button>
    );
  };
  
  /* Pagination Component */
  const PaginationControls = ({ currentPage, lastPage, onPageChange }) => {
    // Generate array of pages to show
    const generatePageNumbers = () => {
      const pages = [];
      
      // Show max 5 page numbers (including current page)
      const maxPagesToShow = 5;
      
      // Calculate start and end page numbers
      let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
      let endPage = startPage + maxPagesToShow - 1;
      
      // Adjust if end page exceeds last page
      if (endPage > lastPage) {
        endPage = lastPage;
        startPage = Math.max(1, endPage - maxPagesToShow + 1);
      }
      
      // Add page numbers
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
      
      return pages;
    };
    
    const pageNumbers = generatePageNumbers();
    
    // Early return if only one page
    if (lastPage <= 1) return null;
    
    return (
      <div className="flex items-center justify-center gap-2">
        {/* Previous Button */}
        <button
          className="w-10 h-10 rounded-lg flex items-center justify-center transition-colors duration-300 text-gray-600 hover:bg-primary/10"
          style={{ 
            borderWidth: '1px',
            borderColor: colors.brown + '40',
            borderStyle: 'solid'
          }}
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          <ChevronLeft size={18} style={{ color: currentPage === 1 ? '#ccc' : colors.brown }} />
        </button>
        
        {/* First Page (if not in view) */}
        {pageNumbers[0] > 1 && (
          <>
            <PageNumber page={1} currentPage={currentPage} onClick={onPageChange} />
            {pageNumbers[0] > 2 && (
              <span className="px-1 text-gray-400">...</span>
            )}
          </>
        )}
        
        {/* Page Numbers */}
        {pageNumbers.map(page => (
          <PageNumber 
            key={page} 
            page={page} 
            currentPage={currentPage} 
            onClick={onPageChange} 
          />
        ))}
        
        {/* Last Page (if not in view) */}
        {pageNumbers[pageNumbers.length - 1] < lastPage && (
          <>
            {pageNumbers[pageNumbers.length - 1] < lastPage - 1 && (
              <span className="px-1 text-gray-400">...</span>
            )}
            <PageNumber page={lastPage} currentPage={currentPage} onClick={onPageChange} />
          </>
        )}
        
        {/* Next Button */}
        <button
          className="w-10 h-10 rounded-lg flex items-center justify-center transition-colors duration-300 text-gray-600 hover:bg-primary/10"
          style={{ 
            borderWidth: '1px',
            borderColor: colors.brown + '40',
            borderStyle: 'solid'
          }}
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === lastPage}
        >
          <ChevronRight size={18} style={{ color: currentPage === lastPage ? '#ccc' : colors.brown }} />
        </button>
      </div>
    );
  };
  const PropertyCard = ({ property }) => {
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [isHovered, setIsHovered] = useState(false);
    const [isFavorite, setIsFavorite] = useState(false);
    const [isTouched, setIsTouched] = useState(false); // For touch devices

    const nextImage = (e) => {
      e.stopPropagation();
      setCurrentImageIndex((prevIndex) => 
        prevIndex === property.images.length - 1 ? 0 : prevIndex + 1
      );
    };

    const prevImage = (e) => {
      e.stopPropagation();
      setCurrentImageIndex((prevIndex) => 
        prevIndex === 0 ? property.images.length - 1 : prevIndex - 1
      );
    };

    const toggleFavorite = (e) => {
      e.stopPropagation();
      setIsFavorite(!isFavorite);
    };

    // For touch devices
    const handleTouchStart = () => {
      setIsTouched(true);
      // Auto-hide touch controls after 3 seconds
      setTimeout(() => {
        setIsTouched(false);
      }, 3000);
    };

    // Handle case where property has no images
    const hasImages = property.images && property.images.length > 0;
    const placeholderImage = "/api/placeholder/400/320"; // Fallback image

    return (
      <div 
        className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 group relative h-full"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onTouchStart={handleTouchStart}>
       
        <div className="relative overflow-hidden h-48 sm:h-56 md:h-64">
          {/* Image Gallery */}
          <img 
            src={hasImages ? property.images[currentImageIndex] : placeholderImage} 
            alt={`${property.name} - Image ${currentImageIndex + 1}`} 
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            onError={(e) => {
              e.target.onerror = null; // Prevent infinite loop
              e.target.src = placeholderImage;
            }}
          />
          
          {/* Image Navigation Arrows - Only show if there are multiple images */}
          {hasImages && property.images.length > 1 && (isHovered || isTouched) && (
            <>
              <button 
                onClick={prevImage}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center bg-white/80 backdrop-blur-sm rounded-full shadow-md transition-all duration-300 hover:bg-white z-10"
              >
                <ChevronLeft className="w-5 h-5" style={{ color: colors.brown }} />
              </button>
              
              <button 
                onClick={nextImage}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center bg-white/80 backdrop-blur-sm rounded-full shadow-md transition-all duration-300 hover:bg-white z-10"
              >
                <ChevronRight className="w-5 h-5" style={{ color: colors.brown }} />
              </button>
            </>
          )}
          
          {/* Image Counter - Only show if there are multiple images */}
          {hasImages && property.images.length > 1 && (
            <div className="absolute bottom-3 right-3 flex items-center bg-white/80 backdrop-blur-sm rounded-full px-2 py-1 shadow-md">
              <Camera className="w-3 h-3 mr-1" style={{ color: colors.brown }} />
              <span className="text-xs font-medium">{currentImageIndex + 1}/{property.images.length}</span>
            </div>
          )}
          
          {/* Gradient Overlay */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent h-16"></div>
        </div>
        
        <div className="p-4 sm:p-5">
          <div className="flex items-start justify-between mb-2">
            <h3 className="text-lg sm:text-xl font-semibold font-ivyMode text-primary">
              {property.name}
            </h3>
            <p className="font-bold text-sm sm:text-base text-secondary">
              {property.price}
            </p>
          </div>
          
          <div className="flex items-center mb-2 sm:mb-3">
            <MapPin className="w-3 h-3 sm:w-4 sm:h-4 mr-1 text-third" />
            <p className="text-xs sm:text-sm font-futura text-gray-500">
              {property.location}
            </p>
          </div>
          
          <p className="text-xs sm:text-sm mb-3 sm:mb-4 font-futura text-start line-clamp-3 text-gray-500">
            {property.description}
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen font-sans text-start">
      {/* Header/Navigation */}
      <NavBar />
      
      {/* Hero Section - Made Responsive */}
      <div className="relative py-12 sm:py-16 md:py-20 overflow-hidden">
        {/* Luxurious Background Elements */}
        <div className="absolute inset-0 bg-white z-0"></div>

        {/* Soft Glow Background - Adjusted size for mobile */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] sm:w-[450px] sm:h-[450px] md:w-[600px] md:h-[600px] bg-primary/10 rounded-full blur-3xl pointer-events-none"></div>

        {/* Background Texture Overlay */}
        <div
          className="absolute inset-0 z-5 opacity-5 mix-blend-overlay"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23a3582a' fill-opacity='0.4' fill-rule='evenodd'%3E%3Ccircle cx='3' cy='3' r='1'/%3E%3Ccircle cx='13' cy='13' r='1'/%3E%3C/g%3E%3C/svg%3E")`,
            backgroundSize: "8px 8px",
          }}
        ></div>

        {/* Subtle Diagonal Line Accents - hidden on smallest screens */}
        <div className="absolute top-0 left-1/4 h-full w-px bg-gradient-to-b from-transparent via-primary/10 to-transparent hidden sm:block"></div>
        <div className="absolute top-0 right-1/4 h-full w-px bg-gradient-to-b from-transparent via-primary/15 to-transparent hidden sm:block"></div>

        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="text-center reveal-item">
            <div className="inline-block px-4 sm:px-6 py-1 sm:py-2 rounded-full mb-4 sm:mb-8 bg-white/80 backdrop-blur-md border border-primary/20 shadow-md">
              <p
                className="font-futura text-xs sm:text-sm flex items-center"
                style={{ color: colors.brown }}
              >
                <span
                  className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full mr-1.5 sm:mr-2"
                  style={{ backgroundColor: colors.brown }}
                ></span>
                Luxury Living Redefined
              </p>
            </div>

            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-light mb-4 sm:mb-6 tracking-tight staggered-title font-ivyMode" style={{ color: "#A3582A" }}>
              <span className="block reveal-heading">Extraordinary Estates</span>
              <span className="relative inline-block reveal-heading delay-200">
                <span className="absolute -rotate-2 inset-0 rounded-md heading-background" style={{ backgroundColor: 'rgba(233, 186, 103, 0.2)' }}></span>
                <span className="relative" style={{ color: "#A3582A" }}>For Exceptional Lives</span>
              </span>
            </h2>

            <p
              className="text-sm sm:text-base md:text-lg lg:text-xl max-w-3xl mx-auto reveal-paragraph font-futura"
              style={{ color: colors.gray }}
            >
              Discover our meticulously curated collection of Dubai's most prestigious properties,
              each embodying the perfect harmony of opulence, comfort, and architectural distinction.
            </p>
          </div>
        </div>
      </div>
      
      {/* Featured Properties Section - Responsive Grid */}
      <section className="py-12 sm:py-16 md:py-20 lg:py-24 bg-white relative overflow-hidden">
        {/* Subtle Background Elements */}
        <div className="absolute top-0 left-0 w-full h-32 sm:h-64"></div>
        <div className="absolute bottom-0 right-0 w-1/3 h-1/3 bg-primary/10 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          {/* Loading State */}
          {isLoading && (
            <div className="flex justify-center items-center py-20">
              <Loader className="animate-spin mr-2" style={{ color: colors.brown }} size={24} />
              <p className="text-lg" style={{ color: colors.gray }}>Loading properties...</p>
            </div>
          )}
          
          {/* Error State */}
          {error && (
            <div className="p-6 bg-red-50 rounded-lg text-center">
              <p className="text-red-600 mb-2 font-medium">Oops! Something went wrong.</p>
              <p className="text-gray-600">{error}</p>
              <button 
                className="mt-4 px-4 py-2 rounded-lg text-white"
                style={{ backgroundColor: colors.brown }}
                onClick={() => window.location.reload()}
              >
                Try Again
              </button>
            </div>
          )}
          
          {/* No Properties Found State */}
          {!isLoading && !error && properties.length === 0 && (
            <div className="p-6 bg-gray-50 rounded-lg text-center">
              <p className="text-gray-600 mb-2 font-medium">No properties found</p>
              <p className="text-gray-500">Check back later for new listings.</p>
            </div>
          )}
          
          {/* Property Cards Grid - Responsive Column Layout */}
          {!isLoading && !error && properties.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
              {properties.map((property) => (
                <PropertyCard key={property.id} property={property} />
              ))}
            </div>
          )}
          
          {/* Pagination Controls */}
          {!isLoading && !error && properties.length > 0 && (
            <div className="mt-10 sm:mt-16 text-center">
              <PaginationControls 
                currentPage={pagination.currentPage} 
                lastPage={pagination.lastPage} 
                onPageChange={handlePageChange} 
              />
              
              <p className="mt-4 text-xs text-gray-500">
                Showing {properties.length} of {pagination.total} properties
              </p>
            </div>
          )}
        </div>
      </section>
      
      {/* CSS Animations */}
      <style jsx>{`
        /* Reveal Animations */
        .reveal-item {
          opacity: 0;
          animation: revealUp 0.8s ease-out forwards;
        }

        .reveal-heading {
          opacity: 0;
          animation: revealLeft 0.8s ease-out forwards;
        }

        .delay-200 {
          animation-delay: 0.2s;
        }

        .delay-400 {
          animation-delay: 0.4s;
        }

        .reveal-paragraph {
          opacity: 0;
          animation: revealUp 0.8s ease-out forwards 0.4s;
        }

        @keyframes revealUp {
          from {
            transform: translateY(30px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        @keyframes revealLeft {
          from {
            transform: translateX(-30px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        /* Pulse Animation */
        .pulse-animation {
          animation: pulse 2s infinite;
        }

        @keyframes pulse {
          0% {
            transform: scale(0.95);
            box-shadow: 0 0 0 0 rgba(163, 88, 42, 0.7);
          }

          70% {
            transform: scale(1);
            box-shadow: 0 0 0 10px rgba(163, 88, 42, 0);
          }

          100% {
            transform: scale(0.95);
            box-shadow: 0 0 0 0 rgba(163, 88, 42, 0);
          }
        }
        
        /* For mobile touch handling */
        @media (hover: none) {
          .group:active .group-hover\:scale-105 {
            transform: scale(1.05);
          }
        }
      `}</style>
    </div>
  );
};

export default PropertiesPage;